import { ActivatedRoute, Router } from '@angular/router';
import { Asset } from '../../../models/asset/asset';
import { AssetProxyService } from '../../../proxies/asset/asset.proxy.service';
import { AssetType } from '../../../models/asset/assetType';
import { AssetTypeProxyService } from '../../../proxies/asset/asset-type.proxy.service';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { EnumService } from '../../../services/enum.service';
import { BreadcrumbService, EnumValues, Note, UserLite, EnumObjectExtended } from '@card-software/library';
import { ApiResponse, AuthService, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { environment } from '../../../../environments/environment';
import { FabricCategory } from '../../../models/fabric/fabricCategory';
import { FabricCategoryProxyService } from '../../../proxies/item/fabric-category.proxy.service';
import { FabricComposition, Greige, S3Object } from '../../../models/greige/greige';
import { Observable, forkJoin, map, of } from 'rxjs';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { GreigeProxyService } from '../../../proxies/item/greige.proxy.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PostSearch } from '../../../models/asset/search';
import { YarnCalculationsService } from '../../../services/yarn-calculations.service';
import { YarnProxyService } from '../../../proxies/item/yarn.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { Translation } from '../../../models/Translations';
import { TranslateService } from '@ngx-translate/core';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import {
  ProductDevelopmentAdminGroup,
  ProductDevelopmentEditorGroup,
  ProductDevelopmentViewerGroup,
} from '../../../models/authorization/enum/user-group.enum';
import { CompositionProxyService } from '../../../proxies/item/composition-proxy.service';
import { Composition } from '../../../models/composition/composition';
import { LocaleHelperService } from '../../../services/localehelperservice.service';
import { ProcessLossPercentageUtil } from '../../../utilities/process-loss-percentage.util';
import { ProductDevelopmentStatus, getValidStatusOptions } from '../../../models/enums/product-development-status';

@Component({
  selector: 'app-greige-details',
  templateUrl: './greige-details.component.html',
  styleUrls: ['./greige-details.component.scss'],
})
export class GreigeDetailsComponent implements OnInit {
  @ViewChild('knittingMachineGraphFile', { static: false })
  inputKGM?: ElementRef<HTMLInputElement>;

  formGroupSubmitted = false;
  isLoading = 0;
  isEditing = false;
  greigeId: string | null = null;
  greige: Greige | null = null;
  yarnPercentageError = false;
  yarnDuplicateLoopLengthError = false;
  categoryEnum: EnumValues;
  subCategoryEnum: EnumValues;
  subSubCategoryEnum: EnumValues;
  notes: Note[] = [];
  userLite: UserLite | null = null;
  translations: Record<string, Translation> = {};
  public allCompositions: Composition[] = [];
  //#region Typeaheads variables

  allAssetTypes: AssetType[] = [];
  allFabricCategories: FabricCategory[] = [];

  //#endregion
  // #region Enums
  statuses: EnumValues;
  statusesEnum?: EnumObjectExtended;

  // #endregion Enums

  //#region Form Controls

  knittingProcessLossPercentage: FormControl<number | null> = new FormControl(null, [
    Validators.min(0.01),
    Validators.max(100),
    Validators.required,
  ]);
  knittingProcessLossInternalPercentageControl = new FormControl<number>({
    value: 0,
    disabled: true,
  });

  fabricCategory = new FormControl<FabricCategory | null>(null, Validators.required);

  assetType = new FormControl<AssetType | null>(null, Validators.required);
  asset = new FormControl<Asset | null>(null, Validators.required);
  diameter: FormControl<number | null> = new FormControl(0, [Validators.min(1), Validators.required]);
  gauge: FormControl<number | null> = new FormControl(0, [Validators.min(1), Validators.required]);
  rpm: FormControl<number | null> = new FormControl(null, [Validators.min(1), Validators.required]);
  feeders: FormControl<number | null> = new FormControl(0, [Validators.min(1), Validators.required]);
  cylinderNeedles: FormControl<number | null> = new FormControl(0, [Validators.min(1), Validators.required]);
  dialNeedles: FormControl<number | null> = new FormControl(null, [Validators.min(1)]);

  shiftA: FormControl<number | null> = new FormControl({
    value: null,
    disabled: true,
  });
  shiftB: FormControl<number | null> = new FormControl({
    value: null,
    disabled: true,
  });
  shiftC: FormControl<number | null> = new FormControl({
    value: null,
    disabled: true,
  });

  totalRevolutionsPerDay: FormControl<number | null> = new FormControl({
    value: null,
    disabled: true,
  });

  totalKgPerDay: FormControl<number | null> = new FormControl({
    value: null,
    disabled: true,
  });
  obtainedEfficiency: FormControl<number | null> = new FormControl(null, [Validators.min(0.01), Validators.max(100), Validators.required]);
  numberOfMachines: FormControl<number | null> = new FormControl(1, [Validators.min(1), Validators.required]);
  totalKgPerDayEfficiency: FormControl<number | null> = new FormControl({
    value: null,
    disabled: true,
  });
  status: FormControl<number> = new FormControl<number>(0, {
    nonNullable: true,
    validators: Validators.required,
  });

  // Form structure
  formGroup = new FormGroup({
    basicInfo: new FormGroup({
      status: this.status,
      wrapsPerInch: new FormControl(0, []),
      coursesPerInch: new FormControl(0, []),
      fabricCategory: this.fabricCategory,
    }),
    machineInfo: new FormGroup({
      assetType: this.assetType,
      asset: this.asset,
      diameter: this.diameter,
      gauge: this.gauge,
      rpm: this.rpm,
      feeders: this.feeders,
      cylinderNeedles: this.cylinderNeedles,
      dialNeedles: this.dialNeedles,
    }),
    yarnInfo: new FormGroup({
      yarnEntries: new FormArray([]),
    }),
    capacityInformation: new FormGroup({
      shiftA: this.shiftA,
      shiftB: this.shiftB,
      shiftC: this.shiftC,
      totalRevolutionsPerDay: this.totalRevolutionsPerDay,
      totalKgPerDay: this.totalKgPerDay,
      obtainedEfficiency: this.obtainedEfficiency,
      numberOfMachines: this.numberOfMachines,
      totalKgPerDayEfficiency: this.totalKgPerDayEfficiency,
    }),
    knittingProcessLossPercentage: this.knittingProcessLossPercentage,
  });

  //#endregion

  // Form getters
  get basicInfo() {
    return this.formGroup.get('basicInfo') as FormGroup;
  }
  get machineInfo() {
    return this.formGroup.get('machineInfo') as FormGroup;
  }
  get yarnInfo() {
    return this.formGroup.get('yarnInfo') as FormGroup;
  }
  get capacityInfo() {
    return this.formGroup.get('capacityInformation') as FormGroup;
  }

  get needles(): number {
    return (this.machineInfo.get('cylinderNeedles')?.value || 0) + (this.machineInfo.get('dialNeedles')?.value || 0);
  }

  get totalKgPerDayEfficiencyValue(): number {
    const efficiency = this.obtainedEfficiency.value || 0;
    const baseTotal = this.totalKgPerDayValue;
    return Math.round(baseTotal * (efficiency / 100));
  }

  get totalKgPerDayValue(): number {
    const rpm = this.rpm.value;
    const numberOfMachines = this.numberOfMachines.value || 0;
    if (!rpm || numberOfMachines <= 0) return 0;

    const rpmPerShift = rpm * 60 * 8;
    const totalRpmPerDay = rpmPerShift * 3;

    let totalKgPerRpm = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.yarnInfo.get('yarnEntries')?.value.forEach((entry: any) => {
      totalKgPerRpm += entry.kgRev || 0;
    });

    const totalKgPerDay = totalKgPerRpm * totalRpmPerDay * numberOfMachines;
    return Math.round(totalKgPerDay * 1000) / 100;
  }

  /**
   * Gets the internal value as a percentage for display
   */
  get knittingProcessLossInternalPercentageDisplay(): number {
    const userPercentage = this.knittingProcessLossPercentage.value;
    if (!userPercentage || userPercentage <= 0) {
      return 0;
    }
    return ProcessLossPercentageUtil.getInternalPercentageDisplay(userPercentage);
  }

  /**
   * Gets the internal value to save to the database
   */
  get knittingProcessLossInternalValueForSave(): number {
    const userPercentage = this.knittingProcessLossPercentage.value;
    if (!userPercentage || userPercentage <= 0) {
      return 0;
    }
    return ProcessLossPercentageUtil.convertUserPercentageToDBValue(userPercentage);
  }
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private greigeService: GreigeProxyService,
    private assetProxyService: AssetProxyService,
    private assetTypeProxyService: AssetTypeProxyService,
    private fabricCategoryProxyService: FabricCategoryProxyService,
    private yarnProxyService: YarnProxyService,
    private yarnCalculationsService: YarnCalculationsService,
    public readonly enumService: EnumService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private userAuthenticationService: UserAuthenticationService,
    private translateService: TranslateService,
    private compositionProxyService: CompositionProxyService,
    private localeHelperService: LocaleHelperService,
    private breadcrumbService: BreadcrumbService,
  ) {
    this.statusesEnum = this.enumService.getEnum('ProductDevelopmentStatus', 'itemEnums');
    this.statuses = this.statusesEnum.values;
    this.categoryEnum = this.enumService.getEnum('Category', 'itemEnums').values;
    this.subCategoryEnum = this.enumService.getEnum('SubCategory', 'itemEnums').values;
    this.subSubCategoryEnum = this.enumService.getEnum('SubSubCategory', 'itemEnums').values;

    this.userLite = this.userAuthenticationService.userLite;
    this.translations = {
      Error_Form_Invalid: {
        es: 'El formulario tiene campos inválidos. Por favor, complete todos los campos requeridos',
        en: 'Form has invalid fields. Please fill all required fields',
      },
    };
  }

  canEdit() {
    // ReadyForProduction is immutable regardless of role (check saved status, not form value)
    const savedStatus = this.greige?.status ?? null;
    if (savedStatus === ProductDevelopmentStatus.ReadyForProduction && this.greigeId !== 'new') {
      return false;
    }

    // Admins can always edit
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.GreigeAdmin)) {
      return true;
    }

    // Editors can edit if saved status < 2 or if it's a new greige
    if (AuthService.hasRole(ProductDevelopmentEditorGroup.GreigeEditor)) {
      if (savedStatus !== null && savedStatus >= 2 && this.greigeId !== 'new') {
        return false;
      }
      return true;
    }

    // Viewers cannot edit
    if (AuthService.hasRole(ProductDevelopmentViewerGroup.GreigeViewer)) {
      return false;
    }

    // Default
    return false;
  }

  //#region Typeahead functions
  public fetchFabricCategories = (query: string): Observable<FabricCategory[]> =>
    this.fabricCategoryTypeahead(query).pipe(
      map((data) =>
        data.map(
          (fc) =>
            ({
              ...fc,
              acFieldToDisplay: `${fc.code} - ${this.localeHelperService.getLocalizedValue(fc.name)}`,
            }) as FabricCategory,
        ),
      ),
    );

  public fetchAssets = (query: string): Observable<Asset[]> => this.assetTypeahead(query).pipe(map((resp) => resp.data?.items || []));

  ngOnInit(): void {
    // Update internal percentage display when user percentage changes
    this.knittingProcessLossPercentage.valueChanges.subscribe((userPercentage) => {
      const displayValue =
        userPercentage && userPercentage > 0 ? ProcessLossPercentageUtil.getInternalPercentageDisplay(userPercentage) : 0;
      this.knittingProcessLossInternalPercentageControl.setValue(displayValue, { emitEvent: false });
    });

    this.route.queryParams.subscribe((params) => {
      this.greigeId = params['id'];
      if (!this.greigeId) {
        this.router.navigate(['/greiges']);
        return;
      }

      this.isLoading++;
      forkJoin([this.assetTypeProxyService.getAll(), this.fabricCategoryProxyService.getFabricCategories()]).subscribe({
        next: ([assetTypes, fabricCategories]) => {
          this.getAllCompositions();
          this.allAssetTypes = assetTypes.data || [];
          this.allFabricCategories =
            fabricCategories.data
              ?.filter((category) => category.isActive)
              .map(
                (fc) =>
                  ({
                    ...fc,
                    acFieldToDisplay: `${fc.code} - ${this.localeHelperService.getLocalizedValue(fc.name)}`,
                  }) as FabricCategory,
              ) || [];
          this.assetType.setValue(this.allAssetTypes.find((at) => at.id === this.asset.value?.assetTypeReference) || null, {
            emitEvent: false,
          });

          this.fabricCategory.setValue(this.allFabricCategories.find((fc) => fc.code === this.greige?.fabricPrefix) || null);
          this.isLoading--;
        },
        error: () => {
          this.isLoading--;
        },
      });

      if (this.greigeId === 'new') {
        this.isEditing = false;
        this.breadcrumbService.setCurrent('Common_New');
        //this.asset.disable();
        this.formGroup.reset();
      } else {
        this.isEditing = true;
        this.loadGreige(this.greigeId);
      }
      //Disable form by Rol
      if (!this.canEdit()) {
        this.formGroup.disable();
      }
    });

    this.rpm.valueChanges.subscribe((rpm) => {
      if (rpm) {
        this.populateMachineCapacity();
      }
    });

    this.obtainedEfficiency.valueChanges.subscribe(() => {
      this.populateMachineCapacity();
    });

    this.numberOfMachines.valueChanges.subscribe(() => {
      this.populateMachineCapacity();
    });

    this.assetType.valueChanges.subscribe((assetType: AssetType | null) => {
      this.asset.setValue(null);
      //this.asset.disable();
      if (assetType?.id) {
        this.asset.enable();
      }
    });

    this.formGroup
      .get('machineInfo')
      ?.get('asset')
      ?.valueChanges.subscribe((asset: Asset | null) => {
        if (asset?.assetId) {
          if (!asset.knittingMachineDetails) {
            // show warning saying that "This asset has not been configured as a knitting machine"
          }
          this.diameter.setValue(asset.knittingMachineDetails?.diameterInches || 0);
          this.gauge.setValue(asset.knittingMachineDetails?.gauge || 0);
          this.feeders.setValue(asset.knittingMachineDetails?.feeders || 0);
          this.cylinderNeedles.setValue(asset.knittingMachineDetails?.cylinderNeedles || 0);
          this.dialNeedles.setValue(asset.knittingMachineDetails?.dialNeedles || null);
        } else {
          this.diameter.setValue(null);
          this.gauge.setValue(null);
          this.feeders.setValue(null);
          this.cylinderNeedles.setValue(null);
          this.dialNeedles.setValue(null);
        }
      });
  }

  getFabricComposition(): FabricComposition[] {
    const yarnEntries = this.yarnInfo.get('yarnEntries')?.value;
    this.getTotalFabricCompositionPercentage(this.yarnCalculationsService.getFabricComposition(yarnEntries));

    return this.yarnCalculationsService.getFabricComposition(yarnEntries);
  }

  getAllCompositions() {
    this.compositionProxyService.getAllCompositions().subscribe({
      next: (response) => {
        this.allCompositions = response.data ?? [];
      },
      error: (error) => {
        console.error('Error fetching compositions:', error);
        this.allCompositions = [];
      },
    });
  }

  getTotalFabricCompositionPercentage(fabricCompositions: FabricComposition[]) {
    const totalComposition = fabricCompositions.reduce((total, composition) => {
      return total + composition.percentage;
    }, 0);

    this.yarnPercentageError = totalComposition != 100;
  }

  checkDuplicateYarnLoopLength(): boolean {
    const yarnEntries = this.yarnInfo.get('yarnEntries')?.value || [];

    // Create a map to track yarn codes with their loop lengths
    const yarnLoopLengthMap = new Map<string, number[]>();

    for (const entry of yarnEntries) {
      const yarnCode = entry.selectedYarn?.code;
      const loopLength = Number(entry.loopLength);

      if (!yarnCode) continue;

      if (!yarnLoopLengthMap.has(yarnCode)) {
        yarnLoopLengthMap.set(yarnCode, []);
      }
      yarnLoopLengthMap.get(yarnCode)!.push(loopLength);
    }

    // Check if any yarn code has duplicate loop lengths
    for (const [, loopLengths] of yarnLoopLengthMap) {
      const uniqueLengths = new Set(loopLengths);
      if (loopLengths.length !== uniqueLengths.size) {
        return true; // Found duplicate loop length for same yarn code
      }
    }

    return false;
  }

  populateMachineCapacity(): void {
    const rpm = this.rpm.value;
    if (!rpm) return;

    const rpmPerShift = rpm * 60 * 8;
    this.shiftA.setValue(rpmPerShift);
    this.shiftB.setValue(rpmPerShift);
    this.shiftC.setValue(rpmPerShift);
    this.totalRevolutionsPerDay.setValue(rpmPerShift * 3);

    // Sum all yarnEntries.kgRev values
    let totalKgPerRpm = 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.yarnInfo.get('yarnEntries')?.value.forEach((yarnEntry: any) => {
      totalKgPerRpm += yarnEntry.kgRev || 0;
    });

    const totalKgPerDay = totalKgPerRpm * rpmPerShift * 3 * (this.numberOfMachines.value || 0);
    // Round to the nearest 0.001 (three decimals)
    this.totalKgPerDay.setValue(Math.round(totalKgPerDay * 1000) / 100);

    const totalKgPerDayEfficiency = totalKgPerDay * ((this.obtainedEfficiency.value || 0) / 100) * (this.numberOfMachines.value || 0);
    this.totalKgPerDayEfficiency.setValue(Math.round(totalKgPerDayEfficiency * 1000) / 100);
  }

  loadGreige(id: string): void {
    this.isLoading++;
    this.greigeService.getGreigeById(id).subscribe({
      next: (resp) => {
        const data = resp.data;
        this.greige = data;
        this.breadcrumbService.setCurrent(data?.greigeCode);
        this.notes = resp.data ? resp.data.notes : [];
        this.currentFileUploaded = resp?.data?.knittingGraphDocuments ?? null;
        this.basicInfo.patchValue({
          greigeCode: data?.greigeCode || '',
          status: data?.status ?? 0,
          wrapsPerInch: data?.wrapsPerInch ?? 0,
          coursesPerInch: data?.coursesPerInch ?? 0,
          fabricCategory: this.allFabricCategories.find((fc) => fc.code === data?.fabricPrefix) || null,
        });
        // Convert internal value to user percentage when loading
        const dbValue = data?.knittingProcessLossPercentage || 0;
        const userPercentage = dbValue > 0 ? ProcessLossPercentageUtil.convertDBValueToUserPercentage(dbValue) : 0;
        this.knittingProcessLossPercentage.setValue(userPercentage);
        // Update internal percentage display
        const displayValue = userPercentage > 0 ? dbValue : 0;
        this.knittingProcessLossInternalPercentageControl.setValue(displayValue);

        if (data?.machineInformation?.assetId) {
          this.isLoading++;
          const searchBody: PostSearch = {
            assetId: data.machineInformation.assetId,
          };
          this.assetProxyService.searchByAssetId(searchBody).subscribe({
            next: (assets) => {
              const asset = assets.data?.items?.find((a) => a.assetId === data.machineInformation?.assetId);
              this.machineInfo.patchValue(
                {
                  assetType: this.allAssetTypes.find((at) => at.id === asset?.assetTypeReference) || null,
                  asset: asset || null,
                  rpm: data.machineInformation?.knittingMachineDetailsSnapshot?.rpm || 0,
                  diameter: data.machineInformation?.knittingMachineDetailsSnapshot?.diameter || 0,
                  gauge: data.machineInformation?.knittingMachineDetailsSnapshot?.gauge || 0,
                  cylinderNeedles: data.machineInformation?.knittingMachineDetailsSnapshot?.cylinderNeedles || 0,
                  dialNeedles: data.machineInformation?.knittingMachineDetailsSnapshot?.dialNeedles || null,
                  feeders: data.machineInformation?.knittingMachineDetailsSnapshot?.feeders || 0,
                },
                { emitEvent: false },
              );
              this.populateMachineCapacity();
              this.isLoading--;
            },
            error: () => {
              this.isLoading--;
            },
          });
        }

        if (data!.yarnDetails.length > 0) {
          forkJoin(
            data!.yarnDetails.map((yd) => {
              return this.yarnProxyService.getYarnByCode(yd.yarnCode!).pipe(
                map((result) => ({
                  selectedYarn: result.data?.items?.find((y) => y.code === yd.yarnCode),
                  cones: yd.cones,
                  count: result.data?.items?.find((y) => y.code === yd.yarnCode)?.yarnCount,
                  usePercentage: yd.usePercentage,
                  yarnPosition: yd.yarnPosition,
                  loopsPerNeedle: yd.loopsPerNeedle,
                  loopLength: yd.loopLength,
                  metersPerRevolution: yd.metersPerRevolution,
                  kilogramsPerRevolution: yd.kilogramsPerRevolution,
                  lbsPerRevolution: yd.poundsPerRevolution,
                })),
              );
            }),
          ).subscribe({
            next: (results) => {
              const yarnEntries = this.yarnInfo.get('yarnEntries') as FormArray;
              yarnEntries.clear();
              results.forEach((result) => {
                yarnEntries.push(
                  new FormGroup({
                    selectedYarn: new FormControl(result.selectedYarn),
                    yarnPosition: new FormControl(result.yarnPosition, Validators.required),
                    loopLength: new FormControl(result.loopLength, [Validators.required, Validators.min(1)]),
                    loopsPerNeedle: new FormControl(result.loopsPerNeedle, [Validators.required, Validators.min(0.01)]),
                    cones: new FormControl(result.cones, [Validators.required, Validators.min(1)]),
                    usePercentage: new FormControl(result.usePercentage, [Validators.required, Validators.min(0.01), Validators.max(100)]),
                    mtsRev: new FormControl(result.metersPerRevolution),
                    kgRev: new FormControl(result.kilogramsPerRevolution),
                    lbsRev: new FormControl(result.lbsPerRevolution),
                    count: new FormControl(result.count),
                  }),
                );
              });

              this.populateMachineCapacity();
            },
            error: (err) => {
              console.error('Error occurred:', err);
            },
          });
        }

        this.capacityInfo.patchValue({
          shiftA: data?.capacityInformation?.shiftA?.revolutions || 0,
          shiftB: data?.capacityInformation?.shiftB?.revolutions || 0,
          shiftC: data?.capacityInformation?.shiftC?.revolutions || 0,
          totalRevolutionsPerDay: data?.capacityInformation?.totalRevolutionsPerDay || 0,
          totalKgPerDay: data?.capacityInformation?.totalKgPerDay || 0,
          totalKgPerDayEfficiency: data?.capacityInformation?.totalKgPerDayEfficiency || 0,
          obtainedEfficiency: data?.capacityInformation?.obtainedEfficiency || 0,
          numberOfMachines: data?.capacityInformation?.numberOfMachines || 0,
        });
        this.populateMachineCapacity();

        // Disable form if status > 2 or user doesn't have edit permission
        if (!this.canEdit()) {
          this.formGroup.disable();
        }

        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
        this.router.navigate(['/greiges']);
      },
    });
  }

  //#region Typahead functions
  fabricCategoryTypeahead = (term: string): Observable<FabricCategory[]> => {
    if (term === '') {
      return of(this.allFabricCategories);
    }
    const categoryEnum = this.enumService.getEnum('Category', 'itemEnums');
    const subCategoryEnum = this.enumService.getEnum('SubCategory', 'itemEnums');
    const subSubCategoryEnum = this.enumService.getEnum('SubSubCategory', 'itemEnums');
    const filtered = this.allFabricCategories.filter(
      (fc) =>
        fc.code?.toLowerCase().includes(term.toLowerCase()) ||
        this.localeHelperService.getLocalizedValue(fc.name)?.toLowerCase().includes(term.toLowerCase()),
    );
    const category = this.allFabricCategories.filter(
      (fc) => fc.category && categoryEnum.values[fc.category]?.toLowerCase().includes(term.toLowerCase()),
    );

    const subCategory = this.allFabricCategories.filter(
      (fc) => fc.subCategory && subCategoryEnum.values[fc.subCategory]?.toLowerCase().includes(term.toLowerCase()),
    );
    const subSubCategory = this.allFabricCategories.filter(
      (fc) => fc.subSubCategory && subSubCategoryEnum.values[fc.subSubCategory]?.toLowerCase().includes(term.toLowerCase()),
    );
    return of([...filtered, ...category, ...subCategory, ...subSubCategory]);
  };

  addCommasAndDecimal(value: number): string {
    if (value == null) return '';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  assetTypeahead = (term: string): Observable<ApiResponse<PaginatedApiResponse<Asset>>> => {
    const body: PostSearch = {
      assetId: term.trim(),
      assetTypeReference: this.machineInfo.get('assetType')?.value?.id,
    };
    return this.assetProxyService.searchByAssetId(body);
  };

  assetTypeTypeahead = (term: string): Observable<AssetType[]> => {
    const filteredAssetTypes = this.allAssetTypes.filter((at) => at.assetCategoryReference === environment.knittingMachineCategoryId);
    const filtered = filteredAssetTypes.filter((at) => at.name.toLowerCase().includes(term.toLowerCase()));
    return of(filtered);
  };

  //#endregion
  getInvalidControls(form: FormGroup | FormArray): string[] {
    const invalidControls: string[] = [];

    function traverse(control: AbstractControl, path: string) {
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach((key) => {
          traverse(control.controls[key], path ? `${path}.${key}` : key);
        });
      } else if (control instanceof FormArray) {
        control.controls.forEach((ctrl, index) => {
          traverse(ctrl, `${path}[${index}]`);
        });
      } else if (control.invalid) {
        invalidControls.push(path);
      }
    }

    traverse(form, '');
    return invalidControls;
  }

  onSave(): void {
    this.formGroupSubmitted = true;
    if (this.formGroup.invalid) {
      GlobalMessageUtil.error(this.translateService.instant('Greige_Required_Fields_Error'), '');
      return;
    }

    // Confirm if transitioning to ReadyForProduction
    if (
      this.status.value === ProductDevelopmentStatus.ReadyForProduction &&
      this.greige?.status !== ProductDevelopmentStatus.ReadyForProduction
    ) {
      this.confirmationService.confirm({
        key: 'main',
        header: this.translateService.instant('Common_Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        message:
          this.translateService.instant('Greige_ReadyForProduction_Confirmation') ||
          'This will set the greige to Ready for Production and it will be available for Work Order Creation. This action cannot be undone.',
        rejectLabel: this.translateService.instant('Common_Cancel'),
        rejectButtonProps: {
          label: this.translateService.instant('Common_Cancel'),
          severity: 'secondary',
          outlined: true,
        },
        acceptButtonProps: {
          label: this.translateService.instant('Common_Accept'),
        },
        accept: () => {
          this.executeSave();
        },
      });
      return;
    }

    this.executeSave();
  }

  private executeSave(): void {
    this.isLoading++;
    const basic = this.basicInfo.value;
    const machine = this.machineInfo.value;
    const capacity = this.capacityInfo.value;
    const yarnArray = (this.yarnInfo.get('yarnEntries') as FormArray).controls;
    const yarnDetails = yarnArray.map((ctrl) => {
      const v = ctrl.value;
      return {
        yarnCode: v.selectedYarn?.code || '',
        dataBaseName: v.selectedYarn?.dataBaseName || '',
        articleCode: String(v.selectedYarn?.articleCode) || '',
        cones: Number(v.cones) || 0,
        usePercentage: Number(v.usePercentage) || 0,
        yarnPosition: Number(v.yarnPosition) || 0,
        loopsPerNeedle: Number(v.loopsPerNeedle) || 0,
        loopLength: Number(v.loopLength) || 0,
        metersPerRevolution: Number(v.metersPerRevolution) || 0,
        kilogramsPerRevolution: Number(v.kilogramsPerRevolution) || 0,
        poundsPerRevolution: Number(v.poundsPerRevolution) || 0,
      };
    });
    if (yarnDetails.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'You must add a Yarn',
        key: 'br',
        life: 2500,
      });
      this.isLoading--;
      this.formGroupSubmitted = false;
      return;
    }

    // Check for duplicate yarn codes with same loop length
    this.yarnDuplicateLoopLengthError = this.checkDuplicateYarnLoopLength();
    if (this.yarnDuplicateLoopLengthError) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          this.translateService.instant('Greige_Duplicate_Yarn_Loop_Length_Error') ||
          'Two or more yarns with the same code cannot have the same loop length',
        key: 'br',
        life: 2500,
      });
      this.isLoading--;
      this.formGroupSubmitted = false;
      return;
    }

    const greigeData: Greige = {
      id: this.greige ? this.greige.id : undefined,
      status: Number(basic.status),
      fabricPrefix: basic.fabricCategory.code || '',
      wrapsPerInch: Number(basic.wrapsPerInch) || 0,
      coursesPerInch: Number(basic.coursesPerInch) || 0,
      greigeCode: this.greige?.greigeCode || undefined,
      machineInformation: {
        assetId: machine.asset?.assetId || '',
        knittingMachineDetailsSnapshot: {
          rpm: this.rpm.value || 0,
          gauge: Number(this.machineInfo.get('gauge')?.value) || 0,
          cylinderNeedles: Number(this.machineInfo.get('cylinderNeedles')?.value) || 0,
          dialNeedles: Number(this.machineInfo.get('dialNeedles')?.value),
          diameter: Number(this.machineInfo.get('diameter')?.value) || 0,
          feeders: Number(this.machineInfo.get('feeders')?.value) || 0,
        },
      },
      knittingProcessLossPercentage: this.knittingProcessLossInternalValueForSave,
      yarnDetails: yarnDetails,
      capacityInformation: {
        shiftA: { revolutions: Number(capacity.shiftA) || 0 },
        shiftB: { revolutions: Number(capacity.shiftB) || 0 },
        shiftC: { revolutions: Number(capacity.shiftC) || 0 },
        totalKgPerDay: capacity.totalKgPerDay || 0,
        totalKgPerDayEfficiency: capacity.totalKgPerDayEfficiency || 0,
        obtainedEfficiency: capacity.obtainedEfficiency || 0,
        numberOfMachines: capacity.numberOfMachines || 0,
        totalRevolutionsPerDay: capacity.totalRevolutionsPerDay || 0,
      },
      knittingGraphDocuments: this.currentFileUploaded!,
      fabricComposition: this.getFabricComposition(),
      notes: this.notes,
      longDescription: '',
    };

    if (this.greige && this.greige.id && this.greige.id !== 'new') {
      this.greigeService.updateGreige(this.greige.id, greigeData).subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translateService.instant('Greige_Updated_Successfully').replace('{{name}}', this.greige?.greigeCode || ''),
            this.translateService.instant('Greige_Details_Title'),
          );
          this.isLoading--;
          this.router.navigate(['/greiges']);
        },
        error: (err) => {
          console.log(err);
          this.isLoading--;
        },
      });
    } else {
      this.greigeService.createGreige(greigeData).subscribe({
        next: (response) => {
          GlobalMessageUtil.success(
            this.translateService.instant('Greige_Created_Successfully').replace('{{name}}', response.data?.greigeCode || ''),
            this.translateService.instant('Greige_Details_Title'),
          );

          this.isLoading--;
          this.router.navigate(['/greiges']);
        },
        error: () => {
          this.isLoading--;
        },
      });
    }
    this.formGroupSubmitted = false;
  }

  onCancel(): void {
    this.router.navigate(['/greiges']);
  }

  // #region handle files

  currentFileUploaded: S3Object | null = null;

  onGraphUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) {
      return;
    }
    const file = input.files[0];

    this.greigeService.uploadKnittingMachineGraph(this.greige!.id!, file).subscribe({
      next: (result) => {
        this.currentFileUploaded = result.data;
        if (this.inputKGM) this.inputKGM.nativeElement.value = '';
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('Common_Success'),
          detail: this.translateService.instant('Greige_KnittingGraphFile_Upload_Successfully'),
          key: 'br',
          life: 2500,
        });
      },
      error: () => {
        if (this.inputKGM) this.inputKGM.nativeElement.value = '';
      },
    });

    // console.log('Knitting Machine Graph file:', file);
  }

  removeGraph(): void {
    //this.currentFileUploaded = null;
    this.confirmationService.confirm({
      message: `${this.translateService.instant('Greige_KnittingGraphFile_deleted_Dialog')} ${this.nameFile}?`,
      header: this.translateService.instant('Common_Confirmation'),
      icon: 'pi pi-exclamation-triangle',
      key: 'main',
      rejectLabel: this.translateService.instant('Common_Cancel'),
      acceptLabel: this.translateService.instant('Common_Accept'),
      accept: () => {
        this.greigeService.deleteKnittingMachineGraph(this.greige!.id!, [this.currentFileUploaded!]).subscribe({
          next: (res) => {
            console.log(res);
            this.currentFileUploaded = null;
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('Common_Success'),
              detail: this.translateService.instant('Greige_KnittingGraphFile_deleted_Successfully'),
              key: 'br',
              life: 2500,
            });
          },
        });
      },
      reject: () => {
        return;
      },
    });
  }

  get extensionFile(): string {
    if (!this.currentFileUploaded?.name) return '';
    return this.currentFileUploaded.name.split('.').pop()!;
  }

  get nameFile(): string {
    if (!this.currentFileUploaded?.name) return '';

    const getName = this.currentFileUploaded.name.split('/').pop()!;

    return getName.split('.').shift()!;
  }

  downloadFile() {
    if (!this.currentFileUploaded?.presignedUrl) return;
    window.open(this.currentFileUploaded.presignedUrl, '_blank');
  }

  public get greigeStatusOptions(): EnumValues {
    if (!this.statuses || typeof this.statuses !== 'object') return {};
    if (!this.canEdit() && this.status.value !== ProductDevelopmentStatus.ReadyForProduction) return {};

    // Admins and Editors with edit capability: show valid next statuses
    if (AuthService.hasRole(ProductDevelopmentAdminGroup.GreigeAdmin) || AuthService.hasRole(ProductDevelopmentEditorGroup.GreigeEditor)) {
      const savedStatus = this.greige?.status ?? ProductDevelopmentStatus.Draft;
      return getValidStatusOptions(savedStatus, this.statuses);
    }

    return {};
  }
  // #endregion

  goToInternalLink(): void {
    window.open('https://scribehow.com/shared/Creating_a_Greige_In_Sierra_ERP__WrzveqorThyiwEsXjNa54A');
  }
}
